import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import type { ReactElement } from 'react';
import {
    ThirdPartySystemId,
    UrlPatternsRuleAction,
    isValidUrlPatternString,
    SessionPersistenceType,
} from '@anchorbase/pillar-sdk';
import type {
    CreateProcessorRequest,
    UrlPatternsRule,
    GetProcessorResponse,
    ProcessorConfig,
} from '@anchorbase/pillar-sdk';

export type UrlPatternsRuleGroupEditorValue = {
    action: UrlPatternsRuleAction;
    urlPatternsText: string;
};

export type ProcessorEditorValue = {
    processorName: string;
    reportingDashboardName: string;
    reportingDashboardUrl: string;
    reportingDashboardAllowedUrlPatternsText: string;
    reportingDashboardSessionPersistence: SessionPersistenceType;
    reportingDashboardUrlPatternsRules: readonly UrlPatternsRuleGroupEditorValue[];
    reportingDashboardFetchDefaultAction: UrlPatternsRuleAction;
    reportingDashboardIframeUrlPatterns: readonly UrlPatternsRuleGroupEditorValue[];
    reportingDashboardIframeDefaultAction: UrlPatternsRuleAction;
};

export function createEmptyProcessorEditorValue(): ProcessorEditorValue {
    return {
        processorName: '',
        reportingDashboardName: '',
        reportingDashboardUrl: '',
        reportingDashboardAllowedUrlPatternsText: '',
        reportingDashboardSessionPersistence: 'persistent',
        reportingDashboardUrlPatternsRules: [],
        reportingDashboardFetchDefaultAction: UrlPatternsRuleAction.ALLOW,
        reportingDashboardIframeUrlPatterns: [],
        reportingDashboardIframeDefaultAction: UrlPatternsRuleAction.ALLOW,
    };
}

function buildUrlPatternsRuleGroupEditorValue(
    urlPatternsRuleGroup: UrlPatternsRule
): UrlPatternsRuleGroupEditorValue {
    return {
        action: urlPatternsRuleGroup.action,
        urlPatternsText: buildUrlPatternsText(urlPatternsRuleGroup.urlPatterns),
    };
}

export function createProcessorEditorValueFromResponse(
    processor: GetProcessorResponse
): ProcessorEditorValue {
    const reportingDashboard = processor.processor_config.reporting_dashboard;
    return {
        processorName: processor.processor_name,
        reportingDashboardName: reportingDashboard?.name ?? '',
        reportingDashboardUrl: reportingDashboard?.url ?? '',
        reportingDashboardAllowedUrlPatternsText:
            reportingDashboard?.allowedUrlPatterns.join('\n') ?? '',
        reportingDashboardSessionPersistence:
            reportingDashboard?.sessionPersistence ?? 'persistent',
        reportingDashboardUrlPatternsRules:
            reportingDashboard?.urlPatternsRules?.map(
                buildUrlPatternsRuleGroupEditorValue
            ) ?? [],
        reportingDashboardFetchDefaultAction:
            reportingDashboard?.fetchDefaultAction ?? UrlPatternsRuleAction.ALLOW,
        reportingDashboardIframeUrlPatterns:
            reportingDashboard?.iframeUrlPatterns?.map(
                buildUrlPatternsRuleGroupEditorValue
            ) ?? [],
        reportingDashboardIframeDefaultAction:
            reportingDashboard?.iframeDefaultAction ?? UrlPatternsRuleAction.ALLOW,
    };
}

function hasProcessorReportingDashboardInput(value: ProcessorEditorValue): boolean {
    return (
        value.reportingDashboardName.trim().length > 0 ||
        value.reportingDashboardUrl.trim().length > 0 ||
        value.reportingDashboardAllowedUrlPatternsText.trim().length > 0 ||
        value.reportingDashboardUrlPatternsRules.length > 0 ||
        value.reportingDashboardFetchDefaultAction !== UrlPatternsRuleAction.ALLOW ||
        value.reportingDashboardIframeUrlPatterns.length > 0 ||
        value.reportingDashboardIframeDefaultAction !== UrlPatternsRuleAction.ALLOW
    );
}

export function validateProcessorEditorValue(value: ProcessorEditorValue): string | null {
    if (value.processorName.trim().length === 0) {
        return 'Processor name is required.';
    }
    if (!hasProcessorReportingDashboardInput(value)) {
        return null;
    }
    if (value.reportingDashboardName.trim().length === 0) {
        return 'Reporting dashboard name is required.';
    }
    if (value.reportingDashboardUrl.trim().length === 0) {
        return 'Reporting dashboard URL is required.';
    }
    const allowedUrlPatternValidationMessage = getUrlPatternTextValidationMessage(
        value.reportingDashboardAllowedUrlPatternsText
    );
    if (allowedUrlPatternValidationMessage !== null) {
        return allowedUrlPatternValidationMessage;
    }
    for (const urlPatternsRuleGroup of value.reportingDashboardUrlPatternsRules) {
        const urlPatternsRuleValidationMessage = getUrlPatternTextValidationMessage(
            urlPatternsRuleGroup.urlPatternsText
        );
        if (urlPatternsRuleValidationMessage !== null) {
            return urlPatternsRuleValidationMessage;
        }
    }
    for (const iframeUrlPatternGroup of value.reportingDashboardIframeUrlPatterns) {
        const iframeUrlPatternValidationMessage = getUrlPatternTextValidationMessage(
            iframeUrlPatternGroup.urlPatternsText
        );
        if (iframeUrlPatternValidationMessage !== null) {
            return iframeUrlPatternValidationMessage;
        }
    }
    return null;
}

function buildUrlPatternsText(urlPatterns: readonly string[]): string {
    return urlPatterns.join('\n');
}

function getUrlPatternTextValidationMessage(value: string): string | null {
    const urlPatterns = parseAllowedUrlPatternsText(value);
    if (urlPatterns.length === 0) {
        return 'At least one reporting dashboard URL pattern is required.';
    }
    const invalidPattern = urlPatterns.find(
        (urlPattern) => !isValidUrlPatternString(urlPattern)
    );
    if (invalidPattern !== undefined) {
        return `Invalid URLPattern syntax: ${invalidPattern}`;
    }
    return null;
}

function parseAllowedUrlPatternsText(value: string): string[] {
    return value
        .split(/[\n,]/)
        .map((entry: string) => entry.trim())
        .filter((entry: string) => entry.length > 0);
}

export function buildProcessorCreateRequest(
    value: ProcessorEditorValue
): CreateProcessorRequest {
    return {
        processor_name: value.processorName.trim(),
        processor_config: buildProcessorConfig(value),
    };
}

export function buildProcessorConfig(value: ProcessorEditorValue): ProcessorConfig {
    if (!hasProcessorReportingDashboardInput(value)) {
        return {};
    }
    return {
        reporting_dashboard: {
            type: 'ipg',
            system_id: ThirdPartySystemId.VALOR_US,
            name: value.reportingDashboardName.trim(),
            url: value.reportingDashboardUrl.trim(),
            allowedUrlPatterns: parseAllowedUrlPatternsText(
                value.reportingDashboardAllowedUrlPatternsText
            ),
            sessionPersistence: value.reportingDashboardSessionPersistence,
            promoteSessionCookies: false,
            urlPatternsRules: value.reportingDashboardUrlPatternsRules.map(
                (urlPatternsRuleGroup) => ({
                    action: urlPatternsRuleGroup.action,
                    urlPatterns: parseAllowedUrlPatternsText(
                        urlPatternsRuleGroup.urlPatternsText
                    ),
                })
            ),
            fetchDefaultAction: value.reportingDashboardFetchDefaultAction,
            iframeUrlPatterns: value.reportingDashboardIframeUrlPatterns.map(
                (iframeUrlPatternGroup) => ({
                    action: iframeUrlPatternGroup.action,
                    urlPatterns: parseAllowedUrlPatternsText(
                        iframeUrlPatternGroup.urlPatternsText
                    ),
                })
            ),
            iframeDefaultAction: value.reportingDashboardIframeDefaultAction,
        },
    };
}

export function parseSessionPersistenceType(
    value: string
): SessionPersistenceType {
    switch (value) {
        case SessionPersistenceType.PERSISTENT:
            return SessionPersistenceType.PERSISTENT;
        case SessionPersistenceType.EPHEMERAL:
            return SessionPersistenceType.EPHEMERAL;
        default:
            throw new Error(`Invalid session persistence type: ${value}`);
    }
}

export function parseUrlPatternsRuleAction(
    value: string
): UrlPatternsRuleAction {
    switch (value) {
        case UrlPatternsRuleAction.ALLOW:
            return UrlPatternsRuleAction.ALLOW;
        case UrlPatternsRuleAction.DENY:
            return UrlPatternsRuleAction.DENY;
        default:
            throw new Error(`Invalid fetch URL pattern action: ${value}`);
    }
}

function UrlPatternsRuleEditorSection(props: {
    readonly title: string;
    readonly defaultActionLabel: string;
    readonly defaultActionId: string;
    readonly defaultAction: UrlPatternsRuleAction;
    readonly ruleActionIdPrefix: string;
    readonly rulePatternsIdPrefix: string;
    readonly ruleGroups: readonly UrlPatternsRuleGroupEditorValue[];
    readonly emptyState: string;
    readonly placeholder: string;
    readonly disabled: boolean;
    readonly onDefaultActionChange: (action: UrlPatternsRuleAction) => void;
    readonly onRuleGroupsChange: (
        nextGroups: readonly UrlPatternsRuleGroupEditorValue[]
    ) => void;
}): ReactElement {
    return (
        <>
            <div>
                <Label htmlFor={props.defaultActionId}>{props.defaultActionLabel}</Label>
                <select
                    id={props.defaultActionId}
                    value={props.defaultAction}
                    onChange={(event) =>
                        props.onDefaultActionChange(
                            parseUrlPatternsRuleAction(event.target.value)
                        )
                    }
                    disabled={props.disabled}
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background"
                >
                    <option value={UrlPatternsRuleAction.ALLOW}>Allow</option>
                    <option value={UrlPatternsRuleAction.DENY}>Deny</option>
                </select>
            </div>
            <div className="space-y-3">
                <div className="flex items-center justify-between gap-3">
                    <div>
                        <div className="text-sm font-medium">{props.title}</div>
                        <p className="text-xs text-muted-foreground">
                            Rules are evaluated from top to bottom.
                        </p>
                    </div>
                    <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        disabled={props.disabled}
                        onClick={() =>
                            props.onRuleGroupsChange([
                                ...props.ruleGroups,
                                {
                                    action: UrlPatternsRuleAction.ALLOW,
                                    urlPatternsText: '',
                                },
                            ])
                        }
                    >
                        Add Rule
                    </Button>
                </div>
                {props.ruleGroups.length === 0 ? (
                    <p className="text-xs text-muted-foreground">{props.emptyState}</p>
                ) : (
                    <div className="space-y-3">
                        {props.ruleGroups.map((ruleGroup, groupIndex) => {
                            const validationMessage = getUrlPatternTextValidationMessage(
                                ruleGroup.urlPatternsText
                            );
                            return (
                                <div key={groupIndex} className="space-y-3 rounded-md border p-3">
                                    <div className="flex items-center justify-between gap-3">
                                        <Label htmlFor={`${props.ruleActionIdPrefix}_${groupIndex}_action`}>
                                            Rule {groupIndex + 1}
                                        </Label>
                                        <Button
                                            type="button"
                                            variant="outline"
                                            size="sm"
                                            disabled={props.disabled}
                                            onClick={() =>
                                                props.onRuleGroupsChange(
                                                    props.ruleGroups.filter(
                                                        (_unusedRuleGroup, currentIndex) =>
                                                            currentIndex !== groupIndex
                                                    )
                                                )
                                            }
                                        >
                                            Remove
                                        </Button>
                                    </div>
                                    <div>
                                        <select
                                            id={`${props.ruleActionIdPrefix}_${groupIndex}_action`}
                                            value={ruleGroup.action}
                                            onChange={(event) =>
                                                props.onRuleGroupsChange(
                                                    props.ruleGroups.map(
                                                        (currentRuleGroup, currentIndex) =>
                                                            currentIndex === groupIndex
                                                                ? {
                                                                      ...currentRuleGroup,
                                                                      action:
                                                                          parseUrlPatternsRuleAction(
                                                                              event.target.value
                                                                          ),
                                                                  }
                                                                : currentRuleGroup
                                                    )
                                                )
                                            }
                                            disabled={props.disabled}
                                            className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background"
                                        >
                                            <option value={UrlPatternsRuleAction.ALLOW}>
                                                Allow
                                            </option>
                                            <option value={UrlPatternsRuleAction.DENY}>
                                                Deny
                                            </option>
                                        </select>
                                    </div>
                                    <div>
                                        <Label
                                            htmlFor={`${props.rulePatternsIdPrefix}_${groupIndex}_patterns`}
                                        >
                                            URL Patterns
                                        </Label>
                                        <textarea
                                            id={`${props.rulePatternsIdPrefix}_${groupIndex}_patterns`}
                                            value={ruleGroup.urlPatternsText}
                                            onChange={(event) =>
                                                props.onRuleGroupsChange(
                                                    props.ruleGroups.map(
                                                        (currentRuleGroup, currentIndex) =>
                                                            currentIndex === groupIndex
                                                                ? {
                                                                      ...currentRuleGroup,
                                                                      urlPatternsText:
                                                                          event.target.value,
                                                                  }
                                                                : currentRuleGroup
                                                    )
                                                )
                                            }
                                            placeholder={props.placeholder}
                                            disabled={props.disabled}
                                            rows={3}
                                            className="flex min-h-20 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background"
                                        />
                                        {validationMessage !== null && (
                                            <p className="mt-1 text-xs text-destructive">
                                                {validationMessage}
                                            </p>
                                        )}
                                        <p className="mt-1 text-xs text-muted-foreground">
                                            Separate multiple URL patterns with commas or new lines.
                                        </p>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </>
    );
}

export function ProcessorEditor(props: {
    value: ProcessorEditorValue;
    onChange: (value: ProcessorEditorValue) => void;
    disabled?: boolean;
}): ReactElement {
    const disabled: boolean = props.disabled ?? false;
    const allowedUrlPatternValidationMessage = hasProcessorReportingDashboardInput(
        props.value
    )
        ? getUrlPatternTextValidationMessage(
              props.value.reportingDashboardAllowedUrlPatternsText
          )
        : null;

    return (
        <div className="space-y-4">
            <div>
                <Label htmlFor="processor_name">Processor Name</Label>
                <Input
                    id="processor_name"
                    value={props.value.processorName}
                    onChange={(event) =>
                        props.onChange({
                            ...props.value,
                            processorName: event.target.value,
                        })
                    }
                    placeholder="Processor name"
                    disabled={disabled}
                />
            </div>
            <div className="space-y-4 rounded-md border p-4">
                <div className="font-medium">Reporting Dashboard</div>
                <div>
                    <Label htmlFor="reporting_dashboard_name">Name</Label>
                    <Input
                        id="reporting_dashboard_name"
                        value={props.value.reportingDashboardName}
                        onChange={(event) =>
                            props.onChange({
                                ...props.value,
                                reportingDashboardName: event.target.value,
                            })
                        }
                        placeholder="Reporting dashboard name"
                        disabled={disabled}
                    />
                </div>
                <div>
                    <Label htmlFor="reporting_dashboard_url">URL</Label>
                    <Input
                        id="reporting_dashboard_url"
                        value={props.value.reportingDashboardUrl}
                        onChange={(event) =>
                            props.onChange({
                                ...props.value,
                                reportingDashboardUrl: event.target.value,
                            })
                        }
                        placeholder="https://example.com/dashboard"
                        disabled={disabled}
                    />
                </div>
                <div>
                    <Label htmlFor="reporting_dashboard_allowed_url_patterns">
                        Allowed URL Patterns
                    </Label>
                    <textarea
                        id="reporting_dashboard_allowed_url_patterns"
                        value={props.value.reportingDashboardAllowedUrlPatternsText}
                        onChange={(event) =>
                            props.onChange({
                                ...props.value,
                                reportingDashboardAllowedUrlPatternsText: event.target.value,
                            })
                        }
                        placeholder="https://example.com/*"
                        disabled={disabled}
                        rows={3}
                        className="flex min-h-20 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background"
                    />
                    {allowedUrlPatternValidationMessage !== null && (
                        <p className="mt-1 text-xs text-destructive">
                            {allowedUrlPatternValidationMessage}
                        </p>
                    )}
                    <p className="mt-1 text-xs text-muted-foreground">
                        Separate multiple URL patterns with commas or new lines.
                    </p>
                </div>
                <div>
                    <Label htmlFor="reporting_dashboard_session_persistence">
                        Session Persistence
                    </Label>
                    <select
                        id="reporting_dashboard_session_persistence"
                        value={props.value.reportingDashboardSessionPersistence}
                        onChange={(event) =>
                            props.onChange({
                                ...props.value,
                                reportingDashboardSessionPersistence:
                                    parseSessionPersistenceType(event.target.value),
                            })
                        }
                        disabled={disabled}
                        className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background"
                    >
                        <option value="persistent">Persistent</option>
                        <option value="ephemeral">Ephemeral</option>
                    </select>
                </div>
                <UrlPatternsRuleEditorSection
                    title="Fetch URL Patterns"
                    defaultActionLabel="Fetch Default Action"
                    defaultActionId="reporting_dashboard_fetch_default_action"
                    defaultAction={props.value.reportingDashboardFetchDefaultAction}
                    ruleActionIdPrefix="reporting_dashboard_fetch_rule"
                    rulePatternsIdPrefix="reporting_dashboard_fetch_rule"
                    ruleGroups={props.value.reportingDashboardUrlPatternsRules}
                    emptyState="No fetch URL pattern rules configured."
                    placeholder="https://api.example.com/*"
                    disabled={disabled}
                    onDefaultActionChange={(reportingDashboardFetchDefaultAction) =>
                        props.onChange({
                            ...props.value,
                            reportingDashboardFetchDefaultAction,
                        })
                    }
                    onRuleGroupsChange={(reportingDashboardUrlPatternsRules) =>
                        props.onChange({
                            ...props.value,
                            reportingDashboardUrlPatternsRules,
                        })
                    }
                />
                <UrlPatternsRuleEditorSection
                    title="Iframe URL Patterns"
                    defaultActionLabel="Iframe Default Action"
                    defaultActionId="reporting_dashboard_iframe_default_action"
                    defaultAction={props.value.reportingDashboardIframeDefaultAction}
                    ruleActionIdPrefix="reporting_dashboard_iframe_rule"
                    rulePatternsIdPrefix="reporting_dashboard_iframe_rule"
                    ruleGroups={props.value.reportingDashboardIframeUrlPatterns}
                    emptyState="No iframe URL pattern rules configured."
                    placeholder="https://widgets.example.com/*"
                    disabled={disabled}
                    onDefaultActionChange={(reportingDashboardIframeDefaultAction) =>
                        props.onChange({
                            ...props.value,
                            reportingDashboardIframeDefaultAction,
                        })
                    }
                    onRuleGroupsChange={(reportingDashboardIframeUrlPatterns) =>
                        props.onChange({
                            ...props.value,
                            reportingDashboardIframeUrlPatterns,
                        })
                    }
                />
            </div>
        </div>
    );
}
